import { FC, useMemo, useState, useRef, useEffect } from 'react'
import styles from 'components/features/future/FutureAssumptionsPane/FutureAssumptionsPane.styl'
import { FutureLayerUID, FutureWhen, BuyHomeLayerHint, FutureLayer } from 'components/features/future/types'
import { FuturePanePreset } from 'components/features/future/FuturePanePreset/FuturePanePreset'
import * as yup from 'yup'
import { useLayerSelector, extendCommonSchema, useLayerCheck, useHints } from 'components/features/future/helpers'
import { useFormContext } from 'react-hook-form'
import { FutureInputDecoration } from 'components/features/future/FutureInputDecoration/FutureInputDecoration'
import { FutureWhenInput, FULL_OPTIONS } from 'components/features/future/FutureWhenInput/FutureWhenInput'
import { FutureFieldSlider } from 'components/features/future/FutureField/FutureFieldSlider'
import { maskCurrencyPositive, maskPercentFromZero, maskNumberPositive, maskCurrencyFromZero } from 'components/atoms/inputs/maskedInput'
import { FutureFieldButtons } from 'components/features/future/FutureField/FutureFieldButtons'
import { schemaNumber, schemaNumberInString, schemaString } from 'schemas/baseSchema'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { formatUSCurrency, toPercent } from 'helpers/formatHelpers'
import moment from 'moment'
import { useStateRef } from 'hooks/stateHooks'
import { InputTouchedState } from 'components/atoms/inputs/InputTouchedState/InputTouchedState'
import { memberInfoSelector } from 'selectors/globalSelectors'
import { SelectUSStatePresetController } from 'components/atoms/inputs/SelectUSStatePreset/SelectUSStatePreset'
import { useCollectLayerErrors } from 'components/features/future/hooks/useCollectErrors'
import { ObjectSchema } from 'yup'
import { LayerStatus } from 'components/features/future/LayerStatus/LayerStatus'

const DEFAULT_INTEREST_RATE = '3.50'
const DEFAULT_MORTGAGE_PERIOD_YEARS = 30

const LAYER_SLUG = 'buy_home' as const
type LayerSlugType = typeof LAYER_SLUG
type LayerType = Extract<FutureLayer, { slug: LayerSlugType }>

const schemaCreator = extendCommonSchema<LayerSlugType>(yup.object({
  purchase_price: schemaNumber,
  down_payment: schemaNumberInString,
  mortgage_period_years: schemaNumber,
  interest_rate: schemaNumberInString,

  state_code: schemaString,
  insurance_amount: schemaNumber,
  tax_amount: schemaNumber,
}).transform((values) => {
  if (+values.down_payment === 100) {
    return {
      ...values,
      mortgage_period_years: 0,
      interest_rate: '0',
    }
  }

  return values
}) as ObjectSchema<LayerType>)


type FutureLayerPaneBuyHomeProps = {
  uid?: FutureLayerUID
  onCancel: () => void
}

export const FutureLayerPaneBuyHome: FC<FutureLayerPaneBuyHomeProps> = (props) => {
  const { onCancel, uid } = props
  const layer = useLayerSelector<LayerSlugType>(props)

  const state_code = useStoreSelector((state) => {
    const info = memberInfoSelector(state)
    if (!info || !info.city || !info.city.state || !info.city.state.code) {
      return null
    }
    return info.city.state.code
  })

  const layerWidthDefaults = useMemo(() => ({
    when: {
      slug: 'as_soon_as_possible',
    },
    purchase_price: 250000,
    interest_rate: DEFAULT_INTEREST_RATE,
    mortgage_period_years: DEFAULT_MORTGAGE_PERIOD_YEARS,
    down_payment: '20',
    state_code,
    tax_amount: 4250,
    insurance_amount: 2305,
    ...layer,
  } as typeof layer), [layer, state_code])

  return (
    <FuturePanePreset<LayerSlugType>
      layer={ layerWidthDefaults }
      slug={ LAYER_SLUG }
      name='Buy Home'
      icon='house'
      type='goal'
      schemaCreator={ schemaCreator }
      onCancel={ onCancel }
    >
      <FutureLayerPaneBuyHomeForm uid={ uid } />
    </FuturePanePreset>
  )
}

type FutureLayerPaneBuyHomeFormProps = {
  uid?: FutureLayerUID
}
const FutureLayerPaneBuyHomeForm: FC<FutureLayerPaneBuyHomeFormProps> = (props) => {
  const { uid } = props
  const { watch, setValue, getValues } = useFormContext<LayerType>()
  const setValueRef = useRef(setValue)
  setValueRef.current = setValue
  const getValuesRef = useRef(getValues)
  getValuesRef.current = getValues

  const slug = watch('when.slug') as FutureWhen['slug']
  const [
    state_code,
    purchase_price,
    tax_amount,
    insurance_amount,
  ] = watch(['state_code', 'purchase_price', 'tax_amount', 'insurance_amount'])

  const [hints, setHints] = useState<BuyHomeLayerHint['hints'] | null>(null)
  useHints<LayerSlugType>(setHints, ['purchase_price', 'state_code', 'down_payment', 'interest_rate', 'mortgage_period_years'])

  useLayerCheck(uid)
  const { affordable_price, date, data: layerCheckData } = useStoreSelector((state) => {
    const { data } = state.data.future.layerCheck
    return {
      data,
      affordable_price: null,
      date: null,
      ...data,
    }
  })

  const purchasePriceNote = useMemo(() => {
    if (layerCheckData === null) {
      return null
    }

    const purchasePriceFormatted = formatUSCurrency(purchase_price)
    const cantAffordNote = `You won't be able to buy a ${ purchasePriceFormatted } home based on the current model inputs.`

    if (slug !== 'as_soon_as_possible') {
      if (!affordable_price || affordable_price < 0) {
        return cantAffordNote
      }

      return `You have enough money for a down payment on a ${ formatUSCurrency(affordable_price) } home`
    }

    if (!date) {
      return cantAffordNote
    }

    const dateMoment = moment(date)
    const month = dateMoment.diff(moment(), 'month')
    if (month < 1) {
      return `You can afford to buy a ${ purchasePriceFormatted } home now!`
    }

    return `The earliest you'd be able to buy a home of this value is ${ dateMoment.format('MMM YYYY') }.`
  }, [affordable_price, slug, date, layerCheckData, purchase_price])

  //
  // tax_amount
  //

  const propertyTaxesNote = useMemo(() => {
    const percentAmount = (tax_amount / purchase_price) * 100
    const taxNote = !!percentAmount ? `For this home, ${ formatUSCurrency(tax_amount) } is ${ toPercent(percentAmount) }.` : ''

    if (!state_code || !hints) {
      return `Annual property taxes across the US are 1.17% of your home value. \n${ taxNote }`
    }

    return `Annual property taxes in ${ state_code } are ${ hints.suggested_tax_rate }%. \n${ taxNote }`
  }, [state_code, hints, tax_amount, purchase_price])

  const [taxAmountTouchedRef, setTaxAmountTouched] = useStateRef(!!uid)
  useEffect(() => {
    if (taxAmountTouchedRef.current || !hints) {
      return
    }

    setValueRef.current('tax_amount', Math.round(purchase_price * (parseFloat(hints.suggested_tax_rate) / 100)))
  }, [hints, purchase_price, taxAmountTouchedRef])

  //
  // insurance_amount
  //
  const insuranceNote = useMemo(() => {
    if (!state_code || !hints) {
      return 'The average yearly cost of home insurance across the US is $2,305.'
    }
    return `The estimated yearly cost of insurance for this home is ${ formatUSCurrency(hints.suggested_insurance_amount) }`
  }, [state_code, hints])

  const [insuranceAmountTouchedRef, setInsuranceAmountTouched] = useStateRef(!!uid)
  useEffect(() => {
    if (insuranceAmountTouchedRef.current || !hints) {
      return
    }

    setValueRef.current('insurance_amount', hints.suggested_insurance_amount)
  }, [hints, insuranceAmountTouchedRef])

  //
  // down_payment
  //
  const downPayment = watch('down_payment')
  const downPaymentNote = useMemo(() => {
    const downPaymentValue = (purchase_price * (parseFloat(downPayment) / 100))

    if (downPaymentValue) {
      return `${ downPayment }% of this home's value is ${ formatUSCurrency(downPaymentValue) }.`
    }
    return 'Down payments are usually 20%, but can be as low as 5%.'
  }, [downPayment, purchase_price])

  //
  // total payment
  //
  const withFullDownPayment = +downPayment === 100
  const mortgage_monthly_payment = !hints ? null : hints.mortgage_monthly_payment
  const monthlyPaymentChild = useMemo(() => {
    if (mortgage_monthly_payment === null) {
      return null
    }
    const monthlySubtotal = withFullDownPayment ? 0 : mortgage_monthly_payment
    const total = (+(tax_amount / 12) || 0) + (+(insurance_amount / 12) || 0) + (+monthlySubtotal || 0)
    return (
      <div>
        <div className={ styles.label }>Estimated Payment Amount</div>
        <div className={ styles.note }>This is what you can expect to pay monthly for a home like this</div>
        <div className={ styles.total }>
          { formatUSCurrency(total) }
          <div className={ styles.units }>per month</div>
        </div>
      </div>
    )
  }, [mortgage_monthly_payment, insurance_amount, tax_amount, withFullDownPayment])

  const errors = useCollectLayerErrors<LayerType>()

  const completeChild = useMemo(() => (
    <>
      <h1>
        Goal Achieved. Congratulations!
      </h1>
      Don’t forget to update your Settings:
      <ul>
        <li>Update your Housing Type to "Own Home"</li>
        <li>Link your new home to add it to your Assets</li>
        <li>Link your mortgage account to track your Debts</li>
      </ul>
    </>
  ), [])

  return useMemo(() => (
    <LayerStatus uid={ uid || null } completeContent={ completeChild } skipPartialState type='goal'>
      <FutureWhenInput error={ errors.whenField } options={ FULL_OPTIONS } />

      <FutureFieldSlider
        label='Purchase Price'
        name='purchase_price'
        mask={ maskCurrencyPositive }

        error={ errors.purchase_price }
        note={ purchasePriceNote }

        min={ 0 }
        max={ 2000000 }
        step={ 10000 }
      />

      <FutureFieldButtons
        label='Down Payment'
        name='down_payment'
        mask={ maskPercentFromZero }
        inputMode='decimal'

        error={ errors.down_payment }
        note={ downPaymentNote }

        min={ 0 }
        max={ 100 }
        step={ 1 }
      />

      <FutureFieldSlider
        label='Mortgage Period (years)'
        name='mortgage_period_years'
        mask={ maskNumberPositive }

        error={ errors.mortgage_period_years }
        note='Typical mortgages are 15 or 30 years.'

        min={ 0 }
        max={ 40 }
        step={ 5 }

        isDisabled={ withFullDownPayment }
      />

      <FutureFieldButtons
        label='Interest Rate'
        name='interest_rate'
        mask={ maskPercentFromZero }
        inputMode='decimal'

        error={ errors.interest_rate }
        note='Mortgage interest rates are typically between 3% and 8%.'

        min={ 0 }
        max={ 20 }
        step={ .25 }

        isDisabled={ withFullDownPayment }
      />

      <FutureInputDecoration label='Location' error={ errors.state_code }>
        <SelectUSStatePresetController name='state_code' />
      </FutureInputDecoration>

      <InputTouchedState onChange={ setTaxAmountTouched }>
        <FutureFieldSlider
          label='Property Taxes'
          name='tax_amount'
          mask={ maskCurrencyFromZero }

          error={ errors.tax_amount }
          note={ propertyTaxesNote }

          min={ 0 }
          max={ 10000 }
          step={ 50 }
        />
      </InputTouchedState>

      <InputTouchedState onChange={ setInsuranceAmountTouched }>
        <FutureFieldSlider
          label={ `Annual Homeowner\'s Insurance` }
          name='insurance_amount'
          mask={ maskCurrencyFromZero }

          error={ errors.insurance_amount }
          note={ insuranceNote }

          min={ 0 }
          max={ 10000 }
          step={ 50 }
        />
      </InputTouchedState>

      { monthlyPaymentChild }

    </LayerStatus>
  ), [errors, propertyTaxesNote, purchasePriceNote, insuranceNote, downPaymentNote, monthlyPaymentChild, setInsuranceAmountTouched, setTaxAmountTouched, withFullDownPayment, completeChild, uid])
}
